// context/AuthContext.tsx
"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import api from "@/lib/axiosInstance";
import { useRouter } from "next/navigation";
import { toast } from "sonner";

type User = {
  id: number;
  name: string;
  email: string;
  role: string;
  phone?: string;
  username?: string;
  profileImage?: string;
  street?: string;
  city?: string;
  state?: string;
  stateIso?: string;
  country?: string;
  countryIso?: string;
  // Business Information
  companyName?: string;
  companyDescription?: string;
  rcNumber?: string;
  businessLogoUrl?: string;
  corporateAffairsDocUrl?: string;
  nationalIdDocUrl?: string;
  // Payment Details
  bankName?: string;
  bankAccountNumber?: string;
  bankAccountName?: string;
  // Verification
  verified?: boolean;
  verificationDocuments?: any;
  verificationStatus?: string;
  // Auth method
  authProvider?: "LOCAL" | "GOOGLE";
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  error: string | null;
  logout: () => Promise<void>;
  checkUser: () => Promise<void>;
  setUser: (user: User | null) => void;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  error: null,
  logout: async () => {},
  checkUser: async () => {},
  setUser: () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  const checkUser = async () => {
    setLoading(true);
    try {
      const res = await api.get("/auth/me");
      console.log("/auth/me response:", res.data.user);

      if (res.data?.authenticated && res.data?.user) {
        setUser(res.data.user);
        setError(null);
        
        // Redirect to complete profile if needed
        if (res.data.requiresCompletion && typeof window !== 'undefined') {
          const currentPath = window.location.pathname;
          if (currentPath !== '/auth/complete-profile') {
            window.location.href = '/auth/complete-profile';
          }
        }
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error("checkUser error:", err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Check user on mount
  useEffect(() => {
    checkUser();
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
      toast.success("Logged out successfully");
      setUser(null);
      router.push("/auth/login");
    } catch (err) {
      console.error("Logout failed:", err);
      toast.error("Logout failed. Please try again.");
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, error, logout, checkUser, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}
